// src/pages/health/WorkoutPrescriptionVideo.jsx
import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLoadingGate } from "../../../../hooks/useLoadingGate";
import AuthCard from "../../../../components/auth/AuthCard";
import BottomNav from "../../../../components/common/BottomNav";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import hypertensionImg from "../../../../assets/images/hypertension.png";

/** 드롭다운 옵션 */
const CATEGORY_SETS = [
    { key: "dm", label: "당뇨병" },
    { key: "htn", label: "고혈압" },
    { key: "dys", label: "이상지질혈증" },
    { key: "obesity", label: "비만" },
    { key: "gout", label: "통풍" },
];

/** 질환별 콘텐츠 */
const CONTENTS = {
    htn: {
        title: "고혈압 환자를 위한 운동 및 주의사항",
        image: hypertensionImg,
        body: `
혈압이 정상인 사람은 운동을 하면 심박수가 증가하면서 수축기 혈압은 어느 정도까지 상승하나 확장기 혈압은 상승하지 않습니다. 그러나 고혈압 환자는 운동을 하면 수축기 혈압이 정상인에 비해 훨씬 많이 상승하면서, 확장기 혈압도 함께 상승하게 됩니다.

안정시 뿐만 아니라 운동 중에도 혈압이 너무 높이 올라가면 대단히 위험하므로 고혈압 환자라도 수축기 혈압이 210mmHg 이상, 확장기 혈압이 120mmHg 이상 절대 올라가지 않도록 운동강도를 조절해야만 합니다. 그러므로 안정시 혈압이 210/120mmHg 이상 올라가는 고혈압 4단계에 해당하는 사람은 운동을 실시하면 운동의 효과보다는 오히려 위험을 초래하기가 쉽습니다.  때문에 먼저 약물요법을 실시하여 혈압을 안정적으로 감소시켜 놓고 혈압이 안정적으로 잘 유지될 때 운동을 실시해야 합니다.

동일한 운동을 해도 주당 1회 20분하는 운동은 운동으로써 전혀 효과를 기대할 수 없으며, 적어도 주당 3회 이상 30분씩 200∼300칼로리가 소비될 수 있을 정도로 6개월 정도 규칙적으로 운동을 했을 때 비로소 혈압강하 효과를 볼 수 있습니다.
    `.trim(),
    },
};

export default function MetabolicGuide() {
    const ready = useLoadingGate();
    const navigate = useNavigate();

    // 드롭다운 상태
    const [open, setOpen] = useState(false);
    const [catKey, setCatKey] = useState(CATEGORY_SETS[1].key); // 기본: 고혈압
    const selectRef = useRef(null);

    const current = useMemo(
        () => CATEGORY_SETS.find((c) => c.key === catKey) ?? CATEGORY_SETS[0],
        [catKey]
    );

    const handlePick = (key) => {
        setCatKey(key);
        setOpen(false);
    };

    // 바깥 클릭 시 닫힘
    useEffect(() => {
        const onClickOutside = (e) => {
            if (!open) return;
            if (selectRef.current && !selectRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        window.addEventListener("mousedown", onClickOutside);
        return () => window.removeEventListener("mousedown", onClickOutside);
    }, [open]);

    // 키보드 접근성
    const onButtonKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
        }
        if (e.key === "Escape") setOpen(false);
    };

    // 현재 선택된 콘텐츠
    const content = CONTENTS[catKey];

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 md:px-8 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard
                    className="flex-1 w-full mx-auto"
                    title={"대사질환 가이드"}
                    onClose={() => navigate(-1)}
                >
                    <div className="flex flex-col flex-1 pt-3 md:pt-4 pb-24 md:pb-28">
                        {/* 드롭다운 */}
                        <div className="relative" ref={selectRef}>
                            <button
                                type="button"
                                onClick={() => setOpen((v) => !v)}
                                onKeyDown={onButtonKeyDown}
                                className="w-full h-12 md:h-14 px-4 rounded-xl bg-gray-100 border border-gray-200 text-gray-900 flex items-center justify-between shadow-sm"
                                aria-haspopup="listbox"
                                aria-expanded={open}
                                aria-controls="metabolic-guide-dropdown"
                            >
                                <span className="font-medium">{current.label} 가이드</span>
                                <ChevronDownIcon
                                    className={`w-5 h-5 text-gray-700 transition-transform ${open ? "rotate-180" : ""}`}
                                />
                            </button>

                            {open && (
                                <ul
                                    id="metabolic-guide-dropdown"
                                    role="listbox"
                                    className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
                                >
                                    {CATEGORY_SETS.map((c) => {
                                        const selected = catKey === c.key;
                                        return (
                                            <li key={c.key} role="option" aria-selected={selected}>
                                                <button
                                                    className={`w-full text-left h-11 px-4 transition-colors ${selected
                                                        ? "bg-[#E8F1FF] text-[#1F4FD3] font-semibold"
                                                        : "hover:bg-gray-100"
                                                        }`}
                                                    onClick={() => handlePick(c.key)}
                                                >
                                                    {c.label} 가이드
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        {/* 콘텐츠 카드 */}
                        {content && (
                            <article className="mt-4 md:mt-5 rounded-2xl border border-[#F1F1F1] bg-white overflow-hidden">
                                {/* 이미지 */}
                                {content.image && (
                                    <div className="w-full aspect-[16/9] bg-gray-50 flex items-center justify-center">
                                        <img
                                            src={content.image}
                                            alt={content.title}
                                            className="h-full w-full object-contain"
                                            loading="lazy"
                                        />
                                    </div>
                                )}

                                {/* 텍스트 */}
                                <div className="px-4 md:px-6 py-4 md:py-5">
                                    <h3 className="text-xl md:text-2xl font-extrabold text-gray-900 text-center">
                                        {content.title}
                                    </h3>
                                    <div className="border-t boder-[#F1F1F1] m-4" />
                                    <p className="text-base md:text-lg mt-3 text-gray-500 leading-7 whitespace-pre-line">
                                        {content.body}
                                    </p>
                                </div>
                            </article>
                        )}

                        {/* 다른 질환은 준비 중 메시지(선택 사항) */}
                        {!content && (
                            <div className="mt-6 text-gray-500 text-center">준비 중입니다.</div>
                        )}
                    </div>
                </AuthCard>
            )}

            <BottomNav className="h-16 md:h-[72px]" />
        </main>
    );
}
